import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Activity, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Loader2,
  Play,
  Zap
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface AgentJobRun {
  id: string;
  job_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  result: any;
  user_id: string;
}

const jobTypeLabels: Record<string, string> = {
  full_scan: 'Full Scan',
  monitor: 'Monitor Transactions',
  reconcile: 'Reconcile Accounts',
  forecast: 'Predict Cash Flow',
  insights: 'Generate Insights',
};

const statusConfig: Record<string, { icon: typeof CheckCircle; color: string; bg: string }> = {
  completed: { icon: CheckCircle, color: 'text-success', bg: 'bg-success/10' },
  running: { icon: Loader2, color: 'text-primary', bg: 'bg-primary/10' },
  failed: { icon: XCircle, color: 'text-destructive', bg: 'bg-destructive/10' },
  pending: { icon: Clock, color: 'text-warning', bg: 'bg-warning/10' },
};

export function AgentJobsViewer() {
  const [jobs, setJobs] = useState<AgentJobRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggeringJob, setTriggeringJob] = useState(false);

  const fetchJobs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('agent_job_runs')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(50);

    if (!error && data) {
      setJobs(data as AgentJobRun[]);
    }
    setLoading(false);
  };

  const triggerManualScan = async () => {
    setTriggeringJob(true);
    try {
      await supabase.functions.invoke('run-agent-jobs', {
        body: { job_type: 'full_scan', cron: true },
      });
      fetchJobs();
    } finally {
      setTriggeringJob(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    
    const channel = supabase
      .channel('agent-jobs-admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_job_runs' }, () => {
        fetchJobs();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const completedCount = jobs.filter(j => j.status === 'completed').length;
  const failedCount = jobs.filter(j => j.status === 'failed').length;
  const runningCount = jobs.filter(j => j.status === 'running').length;

  return (
    <Card className="rounded-3xl">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Agent Job Runs
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={triggerManualScan}
              disabled={triggeringJob}
              className="rounded-xl"
            >
              {triggeringJob ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Trigger Scan
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchJobs} className="rounded-xl">
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-xl bg-success/10 text-center">
            <div className="text-2xl font-bold text-success">{completedCount}</div>
            <div className="text-xs text-muted-foreground">Completed</div>
          </div>
          <div className="p-3 rounded-xl bg-primary/10 text-center">
            <div className="text-2xl font-bold text-primary">{runningCount}</div>
            <div className="text-xs text-muted-foreground">Running</div>
          </div>
          <div className="p-3 rounded-xl bg-destructive/10 text-center">
            <div className="text-2xl font-bold text-destructive">{failedCount}</div>
            <div className="text-xs text-muted-foreground">Failed</div>
          </div>
        </div>

        {/* Jobs List */}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No job runs yet
            </div>
          ) : (
            <div className="space-y-2">
              {jobs.map((job) => {
                const config = statusConfig[job.status] || statusConfig.pending;
                const Icon = config.icon;
                const duration = job.completed_at && job.started_at
                  ? Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)
                  : null;

                return (
                  <div
                    key={job.id}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface transition-colors"
                  >
                    <div className={cn('p-2 rounded-xl', config.bg)}>
                      <Icon className={cn('h-4 w-4', config.color, job.status === 'running' && 'animate-spin')} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {jobTypeLabels[job.job_type] || job.job_type}
                        </span>
                        <Badge variant="outline" className="text-xs rounded-lg">
                          {job.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{formatDistanceToNow(new Date(job.started_at), { addSuffix: true })}</span>
                        {duration !== null && (
                          <>
                            <span>•</span>
                            <span>{duration}s</span>
                          </>
                        )}
                      </div>
                    </div>
                    {job.result && (
                      <Badge variant="secondary" className="text-xs rounded-lg">
                        {job.result.events_created || 0} events
                      </Badge>
                    )}
                    {job.error_message && (
                      <Badge variant="destructive" className="text-xs rounded-lg max-w-[100px] truncate">
                        {job.error_message}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
